import React from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  FormControl,
  FormHelperText,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  Slider,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from "@mui/material";
import ContentCopyRoundedIcon from "@mui/icons-material/ContentCopyRounded";
import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";
import KeyboardArrowDownRoundedIcon from "@mui/icons-material/KeyboardArrowDownRounded";
import KeyboardArrowUpRoundedIcon from "@mui/icons-material/KeyboardArrowUpRounded";
import {
  colorOpacityPercent,
  colorWithBaseHex,
  colorWithOpacityPercent,
  isSectionAnchorTarget,
  manualDestinationLinkError,
  normalizeEditableColor,
  registryFieldError,
  toColorInput,
} from "./inspectorModel";
import { SEGMENT_FIELD_TYPES } from "../segmentRegistry";

function ButtonDestinationEditor({ field, value, options, disabled, onCommit }) {
  const current = String(value || "").trim();
  const currentIsSection = isSectionAnchorTarget(current);
  const selectedSection = currentIsSection && options.some((option) => option.value === current)
    ? current
    : "";
  const [linkDraft, setLinkDraft] = React.useState(currentIsSection ? "" : current);
  const [linkError, setLinkError] = React.useState("");

  React.useEffect(() => {
    setLinkDraft(currentIsSection ? "" : current);
    setLinkError("");
  }, [current, currentIsSection]);

  const commitLink = () => {
    const nextValue = String(linkDraft || "").trim();
    const error = manualDestinationLinkError(field, nextValue);
    setLinkError(error);
    if (error || nextValue === current) return;
    onCommit(field.path, nextValue);
  };

  const chooseSection = (nextValue) => {
    setLinkError("");
    setLinkDraft("");
    if (nextValue === current) return;
    onCommit(field.path, nextValue);
  };

  return (
    <Stack spacing={0.9}>
      <TextField
        fullWidth
        size="small"
        label="Link"
        value={linkDraft}
        disabled={disabled}
        error={Boolean(linkError)}
        inputProps={{ maxLength: field.maxLength }}
        placeholder="https://sitio.com, /ruta, mailto:…"
        helperText={linkError || "Para un sitio, una ruta, email o teléfono. Las secciones de esta landing se eligen abajo."}
        onChange={(event) => {
          setLinkDraft(event.target.value);
          if (linkError) setLinkError("");
        }}
        onKeyDown={(event) => {
          if (event.key !== "Enter" || event.nativeEvent?.isComposing) return;
          event.preventDefault();
          event.stopPropagation();
          if (typeof event.target?.blur === "function") event.target.blur();
          else commitLink();
        }}
        onBlur={commitLink}
      />
      <FormControl fullWidth size="small" disabled={disabled}>
        <InputLabel>O ir a una sección</InputLabel>
        <Select
          label="O ir a una sección"
          value={selectedSection}
          onChange={(event) => chooseSection(event.target.value)}
        >
          <MenuItem value=""><em>Sin sección</em></MenuItem>
          {options.map((option) => (
            <MenuItem key={`${field.path}:${option.value}`} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </Select>
        <FormHelperText>
          Sólo aparecen secciones visibles que existen en esta landing. El ancla se asigna automáticamente.
        </FormHelperText>
      </FormControl>
    </Stack>
  );
}

function RegisteredBehaviorTextField({ field, value, disabled, onCommit }) {
  const current = String(value ?? "");
  const [draft, setDraft] = React.useState(current);
  const [error, setError] = React.useState("");

  React.useEffect(() => {
    setDraft(current);
    setError("");
  }, [current, field.path]);

  const commit = () => {
    const nextValue = String(draft || "").trim();
    const nextError = registryFieldError(field, nextValue);
    setError(nextError);
    if (nextError || nextValue === current) return;
    onCommit(field.path, nextValue);
  };

  return (
    <TextField
      fullWidth
      size="small"
      label={field.label}
      value={draft}
      disabled={disabled}
      error={Boolean(error)}
      inputProps={{ maxLength: field.maxLength }}
      placeholder={field.validator === "calendly" ? "https://calendly.com/tu-negocio/turno" : undefined}
      helperText={error || field.helperText || "Se guarda al salir del campo."}
      onChange={(event) => {
        setDraft(event.target.value);
        if (error) setError("");
      }}
      onKeyDown={(event) => {
        if (event.key !== "Enter" || event.nativeEvent?.isComposing) return;
        event.preventDefault();
        commit();
      }}
      onBlur={commit}
    />
  );
}
function NavbarButtonsEditor({ property, items, options, disabled, onCommit, showIndividualColors, getElementColor, getElementFallback, onElementColorChange, onElementColorReset }) {
  const safeItems = Array.isArray(items) ? items : [];
  const maxItems = Number.isFinite(property?.limits?.maxItems) ? property.limits.maxItems : 8;
  const reachedLimit = safeItems.length >= maxItems;
  const exceededLimit = safeItems.length > maxItems;
  const limitFeedbackId = `${String(property?.path || "navigation-buttons").replace(/[^a-z0-9_-]/gi, "-")}-limit-feedback`;
  const [errors, setErrors] = React.useState({});

  const updateButton = (index, patch) => {
    const nextItems = [...safeItems];
    nextItems[index] = { ...(nextItems[index] || {}), ...patch };
    onCommit(property.path, nextItems);
  };

  const addButton = () => {
    if (safeItems.length >= maxItems) return;
    onCommit(property.path, [
      ...safeItems,
      {
        id: `nav-${Date.now().toString(36)}`,
        label: "",
        behavior: "section",
        target: "",
        megaMenuContent: "inherit",
        megaMenuDisplay: "inherit",
        megaMenuHierarchy: "inherit",
      },
    ]);
  };

  const removeButton = (index) => {
    setErrors((current) => {
      const next = { ...current };
      delete next[index];
      return next;
    });
    onCommit(property.path, safeItems.filter((_, itemIndex) => itemIndex !== index));
  };

  return (
    <Box
      sx={{
        p: 1.25,
        borderRadius: 2.5,
        bgcolor: "#F7F8FA",
        border: "1px solid rgba(25,36,47,.08)",
      }}
    >
      <Stack
        direction="row"
        spacing={1}
        alignItems="flex-start"
        justifyContent="space-between"
        sx={{ mb: reachedLimit ? 0.65 : 1, flexWrap: "wrap", rowGap: 0.85 }}
      >
        <Box sx={{ minWidth: 0, flex: "1 1 190px" }}>
          <Stack direction="row" spacing={0.8} alignItems="center" sx={{ flexWrap: "wrap", rowGap: 0.4 }}>
            <Typography sx={{ color: "#19242F", fontWeight: 900 }}>
              Botones de la navbar
            </Typography>
            <Typography
              component="span"
              variant="caption"
              aria-label={`${safeItems.length} de ${maxItems} botones configurados`}
              sx={{
                px: 0.8,
                py: 0.2,
                borderRadius: 999,
                color: reachedLimit ? "#8A5A00" : "rgba(25,36,47,.58)",
                bgcolor: reachedLimit ? "rgba(237,164,0,.12)" : "rgba(25,36,47,.06)",
                fontWeight: 900,
                whiteSpace: "nowrap",
              }}
            >
              {safeItems.length} / {maxItems}
            </Typography>
          </Stack>
          <Typography variant="caption" sx={{ color: "rgba(25,36,47,.58)" }}>
            Agregá botones libres. Primero definí el texto y después su comportamiento.
          </Typography>
        </Box>
        <Button
          size="small"
          variant="outlined"
          disabled={disabled || reachedLimit}
          aria-describedby={reachedLimit ? limitFeedbackId : undefined}
          onClick={addButton}
          sx={{ borderRadius: 999, textTransform: "none", fontWeight: 850, flexShrink: 0 }}
        >
          Agregar botón
        </Button>
      </Stack>

      {reachedLimit ? (
        <Typography
          id={limitFeedbackId}
          role="status"
          variant="caption"
          sx={{ display: "block", mb: 1, color: "#8A5A00", fontWeight: 750 }}
        >
          {exceededLimit
            ? `Hay ${safeItems.length} botones y el máximo actual es ${maxItems}. Eliminá los que sobren antes de agregar otro.`
            : `Alcanzaste el máximo de ${maxItems} botones. Eliminá uno para agregar otro.`}
        </Typography>
      ) : null}

      {!safeItems.length ? (
        <Typography variant="body2" sx={{ color: "rgba(25,36,47,.58)" }}>
          Todavía no agregaste botones personalizados a la navbar.
        </Typography>
      ) : null}

      <Stack spacing={1.15}>
        {safeItems.map((item, index) => {
          const label = String(item?.label || "");
          const behavior = ["section", "link", "mega-menu"].includes(item?.behavior)
            ? item.behavior
            : "section";
          const target = String(item?.target || "").trim();
          const selectedSection = behavior === "section" && options.some((option) => option.value === target)
            ? target
            : "";
          const linkError = errors[index] || "";

          return (
            <Box key={item?.id || `${property.path}:${index}`} sx={{ p: 1.15, borderRadius: 2.25, bgcolor: "#fff" }}>
              <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between" sx={{ mb: 0.9 }}>
                <Typography variant="caption" sx={{ color: "rgba(25,36,47,.56)", fontWeight: 850 }}>
                  Botón {index + 1}
                </Typography>
                <Button
                  size="small"
                  color="error"
                  disabled={disabled}
                  onClick={() => removeButton(index)}
                  sx={{ minWidth: 0, px: 1, textTransform: "none", fontWeight: 800 }}
                >
                  Quitar
                </Button>
              </Stack>

              <Stack spacing={1}>
                <TextField
                  key={`${item?.id || index}:label:${label}`}
                  fullWidth
                  size="small"
                  label="Texto del botón"
                  defaultValue={label}
                  disabled={disabled}
                  inputProps={{ maxLength: 80 }}
                  helperText="Este es el texto que se verá en la navbar."
                  onBlur={(event) => {
                    const nextValue = event.target.value.trim();
                    if (nextValue !== label) updateButton(index, { label: nextValue });
                  }}
                />

                {showIndividualColors ? (
                  <IndividualTextColorControl
                    fieldPath={`navigation.buttons.${index}.label`}
                    label={`Botón ${index + 1}`}
                    value={getElementColor?.(`navigation.buttons.${index}.label`) || ""}
                    fallback={getElementFallback?.(`navigation.buttons.${index}.label`) || "#000000"}
                    disabled={disabled}
                    onChange={(next) => onElementColorChange?.(`navigation.buttons.${index}.label`, next)}
                    onReset={() => onElementColorReset?.(`navigation.buttons.${index}.label`)}
                  />
                ) : null}

                <FormControl fullWidth size="small" disabled={disabled}>
                  <InputLabel>Comportamiento</InputLabel>
                  <Select
                    label="Comportamiento"
                    value={behavior}
                    onChange={(event) => {
                      setErrors((current) => ({ ...current, [index]: "" }));
                      updateButton(index, { behavior: event.target.value, target: "" });
                    }}
                  >
                    <MenuItem value="section">Deslizar a una sección</MenuItem>
                    <MenuItem value="link">Abrir un link</MenuItem>
                    <MenuItem value="mega-menu">Desplegar mega menú</MenuItem>
                  </Select>
                </FormControl>

                {behavior === "section" ? (
                  <FormControl fullWidth size="small" disabled={disabled || !options.length}>
                    <InputLabel>Sección de destino</InputLabel>
                    <Select
                      label="Sección de destino"
                      value={selectedSection}
                      onChange={(event) => updateButton(index, { target: event.target.value })}
                    >
                      <MenuItem value=""><em>Elegí una sección</em></MenuItem>
                      {options.map((option) => (
                        <MenuItem key={`${item?.id || index}:${option.value}`} value={option.value}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </Select>
                    <FormHelperText>
                      {options.length
                        ? "Sólo aparecen secciones existentes y habilitadas. No se pueden escribir anclas manualmente."
                        : "No hay secciones habilitadas disponibles como destino."}
                    </FormHelperText>
                  </FormControl>
                ) : behavior === "link" ? (
                  <TextField
                    key={`${item?.id || index}:target:${target}`}
                    fullWidth
                    size="small"
                    label="Link"
                    defaultValue={target}
                    disabled={disabled}
                    error={Boolean(linkError)}
                    inputProps={{ maxLength: 2000 }}
                    placeholder="https://sitio.com o /ruta"
                    helperText={linkError || "Podés usar un sitio, una ruta, email o teléfono. Para secciones usá el comportamiento Deslizar."}
                    onBlur={(event) => {
                      const nextValue = event.target.value.trim();
                      const error = manualDestinationLinkError(
                        { type: SEGMENT_FIELD_TYPES.URL, maxLength: 2000 },
                        nextValue
                      );
                      setErrors((current) => ({ ...current, [index]: error }));
                      if (!error && nextValue !== target) updateButton(index, { target: nextValue });
                    }}
                  />
                ) : (
                  <Stack spacing={1}>
                    <FormControl fullWidth size="small" disabled={disabled}>
                      <InputLabel>Contenido del mega menú</InputLabel>
                      <Select
                        label="Contenido del mega menú"
                        value={item?.megaMenuContent || "inherit"}
                        onChange={(event) => updateButton(index, { megaMenuContent: event.target.value })}
                      >
                        <MenuItem value="inherit">Usar configuración general</MenuItem>
                        <MenuItem value="auto">Automático</MenuItem>
                        <MenuItem value="products">Sólo productos</MenuItem>
                        <MenuItem value="services">Sólo servicios</MenuItem>
                        <MenuItem value="mixed">Productos y servicios</MenuItem>
                      </Select>
                    </FormControl>
                    <FormControl fullWidth size="small" disabled={disabled}>
                      <InputLabel>Qué mostrar</InputLabel>
                      <Select
                        label="Qué mostrar"
                        value={item?.megaMenuDisplay || "inherit"}
                        onChange={(event) => updateButton(index, { megaMenuDisplay: event.target.value })}
                      >
                        <MenuItem value="inherit">Usar configuración general</MenuItem>
                        <MenuItem value="categories">Sólo categorías</MenuItem>
                        <MenuItem value="items">Sólo ítems</MenuItem>
                        <MenuItem value="both">Categorías + ítems</MenuItem>
                      </Select>
                    </FormControl>
                    <FormControl fullWidth size="small" disabled={disabled}>
                      <InputLabel>Jerarquía</InputLabel>
                      <Select
                        label="Jerarquía"
                        value={item?.megaMenuHierarchy || "inherit"}
                        onChange={(event) => updateButton(index, { megaMenuHierarchy: event.target.value })}
                      >
                        <MenuItem value="inherit">Usar configuración general</MenuItem>
                        <MenuItem value="category-first">Categoría → subcategoría</MenuItem>
                        <MenuItem value="subcategory-first">Subcategoría → categoría</MenuItem>
                      </Select>
                    </FormControl>
                    <FormHelperText>Este botón abre el catálogo desplegable sin navegar a otra ruta.</FormHelperText>
                  </Stack>
                )}
              </Stack>
            </Box>
          );
        })}
      </Stack>
    </Box>
  );
}
function IndividualTextColorControl({
  fieldPath,
  label,
  value,
  fallback,
  disabled,
  onChange,
  onReset,
}) {
  const effective = normalizeEditableColor(value) || normalizeEditableColor(fallback) || "#000000";
  const pickerHex = toColorInput(effective);
  const opacity = colorOpacityPercent(effective, fallback);
  const [copyFeedback, setCopyFeedback] = React.useState(null);
  const copyFeedbackTimerRef = React.useRef(null);

  const clearCopyFeedbackTimer = React.useCallback(() => {
    if (copyFeedbackTimerRef.current) {
      clearTimeout(copyFeedbackTimerRef.current);
      copyFeedbackTimerRef.current = null;
    }
  }, []);

  const showCopyFeedback = React.useCallback((nextFeedback) => {
    clearCopyFeedbackTimer();
    setCopyFeedback(nextFeedback);
    copyFeedbackTimerRef.current = setTimeout(() => {
      setCopyFeedback(null);
      copyFeedbackTimerRef.current = null;
    }, 1800);
  }, [clearCopyFeedbackTimer]);

  React.useEffect(() => () => clearCopyFeedbackTimer(), [clearCopyFeedbackTimer]);

  React.useEffect(() => {
    clearCopyFeedbackTimer();
    setCopyFeedback(null);
  }, [fieldPath, clearCopyFeedbackTimer]);

  const copyHex = React.useCallback(async () => {
    if (disabled) return;
    const hex = pickerHex.toUpperCase();
    try {
      if (typeof navigator === "undefined" || !navigator.clipboard?.writeText) {
        throw new Error("Clipboard API unavailable");
      }
      await navigator.clipboard.writeText(hex);
      showCopyFeedback("success");
    } catch {
      showCopyFeedback("error");
    }
  }, [disabled, pickerHex, showCopyFeedback]);

  const copyFeedbackText = copyFeedback === "success"
    ? "Copiado"
    : copyFeedback === "error"
      ? "No se pudo copiar"
      : `Copiar ${pickerHex.toUpperCase()}`;

  return (
    <Box
      sx={{
        mt: 0.8,
        p: 1,
        borderRadius: 2.25,
        bgcolor: "rgba(25,36,47,.035)",
        border: "1px solid rgba(25,36,47,.08)",
      }}
    >
      <Typography variant="caption" sx={{ display: "block", mb: 0.7, color: "rgba(25,36,47,.64)", fontWeight: 900 }}>
        Color individual · {label}
      </Typography>
      <Stack direction="row" spacing={0.8} alignItems="center">
        <Box
          component="input"
          type="color"
          aria-label={`Color de ${label}`}
          value={pickerHex}
          disabled={disabled}
          onChange={(event) => onChange?.(colorWithBaseHex(effective, event.target.value, fallback))}
          sx={{
            width: 42,
            height: 38,
            p: 0.2,
            border: "1px solid rgba(25,36,47,.14)",
            borderRadius: 2,
            bgcolor: "transparent",
            cursor: disabled ? "default" : "pointer",
            flexShrink: 0,
            opacity: opacity === 0 ? 0.42 : 1,
          }}
        />
        <TextField
          key={`${fieldPath}:${pickerHex}`}
          size="small"
          fullWidth
          label="HEX exacto"
          defaultValue={pickerHex.toUpperCase()}
          disabled={disabled}
          inputProps={{ maxLength: 7 }}
          helperText={value ? "Color propio de este texto" : "Heredado de la paleta de esta sección"}
          onFocus={(event) => event.target.select()}
          onBlur={(event) => {
            const candidate = String(event.target.value || "").trim();
            if (!/^#[0-9a-f]{3}(?:[0-9a-f]{3})?$/i.test(candidate)) return;
            onChange?.(colorWithBaseHex(effective, candidate, fallback));
          }}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end" sx={{ position: "relative" }}>
                {copyFeedback ? (
                  <Box
                    component="span"
                    role="status"
                    aria-live="polite"
                    aria-atomic="true"
                    sx={{
                      position: "absolute",
                      right: -4,
                      bottom: "calc(100% + 6px)",
                      zIndex: 2,
                      px: 0.8,
                      py: 0.35,
                      borderRadius: 1.25,
                      bgcolor: copyFeedback === "error" ? "error.main" : "success.main",
                      color: "common.white",
                      fontSize: "0.68rem",
                      fontWeight: 850,
                      lineHeight: 1.2,
                      whiteSpace: "nowrap",
                      boxShadow: 1,
                      pointerEvents: "none",
                    }}
                  >
                    {copyFeedbackText}
                  </Box>
                ) : null}
                <Tooltip title={copyFeedbackText}>
                  <span>
                    <IconButton
                      size="small"
                      disabled={disabled}
                      aria-label={`Copiar HEX de ${label}`}
                      onMouseDown={(event) => event.preventDefault()}
                      onClick={copyHex}
                    >
                      <ContentCopyRoundedIcon fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
              </InputAdornment>
            ),
          }}
        />
        <Button
          size="small"
          disabled={disabled || !value}
          onClick={onReset}
          sx={{ minWidth: 0, px: 0.9, textTransform: "none", fontWeight: 850 }}
        >
          Heredar
        </Button>
      </Stack>
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.85 }}>
        <Typography variant="caption" sx={{ minWidth: 92, color: "rgba(25,36,47,.62)", fontWeight: 850 }}>
          Opacidad {opacity}%
        </Typography>
        <Slider
          size="small"
          min={0}
          max={100}
          step={1}
          value={opacity}
          disabled={disabled}
          valueLabelDisplay="auto"
          valueLabelFormat={(next) => `${next}%`}
          aria-label={`Opacidad de ${label}`}
          onChange={(_, next) => onChange?.(colorWithOpacityPercent(effective, Number(next), fallback))}
          sx={{ flex: 1 }}
        />
      </Stack>
    </Box>
  );
}
function LevelSelector({ label, value, options, onChange }) {
  return (
    <Box>
      <Typography variant="body2" sx={{ mb: 0.8, fontWeight: 900 }}>
        {label}
      </Typography>
      <ToggleButtonGroup
        exclusive
        fullWidth
        size="small"
        aria-label={label}
        value={value}
        onChange={(_, next) => next && onChange(next)}
      >
        {options.map((option) => (
          <ToggleButton key={option.value} value={option.value} sx={{ textTransform: "none", fontWeight: 850 }}>
            {option.label}
          </ToggleButton>
        ))}
      </ToggleButtonGroup>
    </Box>
  );
}

function InspectorAccordion({
  id,
  expanded,
  onChange,
  icon: Icon,
  title,
  description,
  children,
}) {
  return (
    <Accordion
      expanded={expanded === id}
      onChange={(_, nextExpanded) => onChange(nextExpanded ? id : false)}
      disableGutters
      elevation={0}
      sx={{
        border: "1px solid rgba(25,36,47,.09)",
        borderRadius: "14px !important",
        bgcolor: "#fff",
        overflow: "hidden",
        "&::before": { display: "none" },
      }}
    >
      <AccordionSummary
        expandIcon={<ExpandMoreRoundedIcon />}
        aria-controls={`segment-inspector-${id}-content`}
        id={`segment-inspector-${id}-header`}
        sx={{
          px: 1.4,
          minHeight: 58,
          "& .MuiAccordionSummary-content": { my: 1 },
        }}
      >
        <Stack direction="row" spacing={1.1} alignItems="center" sx={{ minWidth: 0 }}>
          <Box
            sx={{
              width: 34,
              height: 34,
              borderRadius: 2.5,
              display: "grid",
              placeItems: "center",
              bgcolor: "rgba(243,90,58,.10)",
              color: "#F35A3A",
              flexShrink: 0,
            }}
          >
            <Icon fontSize="small" />
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <Typography sx={{ color: "#19242F", fontWeight: 950, lineHeight: 1.15 }}>
              {title}
            </Typography>
            <Typography variant="caption" sx={{ color: "rgba(25,36,47,.62)", lineHeight: 1.3 }}>
              {description}
            </Typography>
          </Box>
        </Stack>
      </AccordionSummary>
      <AccordionDetails
        id={`segment-inspector-${id}-content`}
        sx={{ px: 1.4, pt: 0.5, pb: 1.5 }}
      >
        {children}
      </AccordionDetails>
    </Accordion>
  );
}

export {
  ButtonDestinationEditor,
  RegisteredBehaviorTextField,
  NavbarButtonsEditor,
  IndividualTextColorControl,
  LevelSelector,
  InspectorAccordion
};
